<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3"
    height="100%"
  >
    <div>    
        <v-dialog v-model="showReportDialog">  
          <v-card>
            <v-card-title>
              <v-btn class="ma-1" color="secondary" v-on:click="() => showReportDialog = false">
                Close
              </v-btn>    
              <v-spacer/>
              <v-btn class="ma-1" color="primary" v-on:click="downloadReport(openedDogId, openedReportType)">
                Download&nbsp;<font-awesome-icon icon="file-download" size="lg" />
              </v-btn>    
            </v-card-title>
            <v-card-text>
              <report-viewer
                    :reportName="Report_Name"
                    :dataUrl="Report_URL"
                    ref="reportViewer"
                    :printOnly="true"
                    :exportsSettings="exportsSettings"
                />    
            </v-card-text>
          </v-card>
        </v-dialog>   
        <div>
          <v-row class="ma-0 mb-3">
            <h2>My Online Dog Reports</h2>
          </v-row>
        </div>
        <v-row>
          <v-alert width="100%" class="mr-5 ml-5" type="info">
            To order another report go to the selected dog profile and click 'Order Report' button.<br/>
            The online access is given immediately after the successful payment.<br/>
            It can be downloaded from the dog profile or from the 'My Information' > 'Dog reports' list (current page).
          </v-alert>
          <v-alert width="100%" class="mr-5 ml-5" v-model="isError" dismissible type="error"> {{ errorMessage }}</v-alert>
        </v-row>   
        <v-data-table 
            dense
            fixed-header
            class="elevation-1 mt-2 mb-2 pt-2"
            :loading="loading"
            :headers="headers" 
            :options.sync="options"
            :items="paginatedResponse.data"
            :server-items-length="paginatedResponse.totalCount"
            >
              <template v-slot:header.dogId="{ header }">
                <v-text-field hide-details
                  v-model="searchParams.dogId"
                  class="mt-2 pt-2 mb-2"
                  label="Dog ID#"
                  clearable
                  @click:clear="searchParams.dogId = ''"
                  v-on:keyup.enter="loadMyPedigreesImmediately"
                />
            </template>
            <template v-slot:header.dogName="{ header }">
                <v-text-field hide-details
                  class="mt-2 pt-2 mb-2"
                  v-model="searchParams.dogName"
                  label="Dog Name"
                  clearable
                  @click:clear="clearDogNameFilter"
                  v-on:keyup.enter="loadMyPedigreesImmediately"
                />
            </template>  
            <template v-slot:header.accessStartsAt="{ header }">
                <v-checkbox
                  v-model="searchParams.includeInactive"
                  label="Access: include inactive"
                />
            </template>
            <template v-slot:item.dogId="{ item }">                           
              <router-link :to="`/dogs/${encodeURIComponent(item.dogId)}`">
                {{ item.dogId }}
              </router-link>
            </template>  
            <template v-slot:item.dogName="{ item }">                           
              <router-link :to="`/dogs/${encodeURIComponent(item.dogId)}`">
                {{ item.dogName }}
              </router-link>
            </template>  
            <template v-slot:item.accessStartsAt="{ item }">              
              <span v-if="!!item.accessStartsAt" :style="item.accessActive == false ? 'color: red;' : 'color: green;'"><localized-date-time :instant="item.accessStartsAt" />
                <span v-if="!!item.accessEndsAt"> - <localized-date-time :instant="item.accessEndsAt" /></span>
              </span>
            </template>
            <template v-slot:item.actionButton="{ item }">  
                <v-btn :disabled="item.accessActive == false" class="ma-1" color="primary" v-on:click="showReport(item.dogId, item.reportType)">Show</v-btn>
                <router-link
                    class="no-text-decoration"
                    :to="`/pedigree/order/${item.dogId.trim()}?dogName=${item.dogName}`"
                    v-if="item.accessActive == false"                   
                  >
                  <v-btn class="ma-1" color="primary">      
                    Order again
                  </v-btn>
                </router-link>
                <router-link
                    class="no-text-decoration"
                    :to="`/pedigree/order/${item.dogId.trim()}?dogName=${item.dogName}`"
                    v-if="item.isPedigree == true"                   
                  >
                  <v-btn class="ma-1" color="primary">      
                    Order print
                  </v-btn>
                </router-link>
            </template>  
        </v-data-table>
    </div>
    </v-sheet>
  </template>
  
  <script lang="ts">
  import Vue from "vue";
  import pedigreeService from "@/services/portal/pedigreeService";
  import { getErrorMessage } from "@/services/errorHandler";
  import { UserInfo } from "@/types/User";
  import ReportViewer from "@/components/Admin/Reports/ReportViewer.vue";
  import { PaginationOptions, PaginatedResponse } from "@/types/Pagination";
  import lookupsService from "@/services/lookupsService";
  import { debounce } from "throttle-debounce";
  import { getUserToday } from "@/helpers/date-utils";
  
  export default Vue.extend({
    name: "MyPedigrees",
    components: {
        ReportViewer
    },
    props: {
      dogId: { type: String },
    },
    data: function() {
      return {
        loading: false,
        loadRequestId: 0,
        loadMyPedigreesForInactiveFilter: null as any,
        loadMyPedigreesForDogFilter: null as any,
        loadMyPedigreesForNameFilter: null as any,
        isError: false,
        errorMessage: "",
        Report_Name: '',
        Report_URL: '',
        openedDogId: null as any,
        openedReportType: null as any,
        exportsSettings: {},
        showReportDialog: false,
        paginatedResponse: { data: [], page: 0, totalCount: 0 } as PaginatedResponse<object>,
        searchParams: {
          dogId: this.$props.dogId,
          dogName: "",    
          includeInactive: false,      
        },
        options: {
          page: 1,
          itemsPerPage: -1,
          sortBy: [],
          sortDesc: [],
          groupBy: [],
          groupDesc: [],
          multiSort: false,
          mustSort: false,
        } as PaginationOptions,
        headers: [
          { text: "Report type", align: "left", value: "reportTypeName", width: "100px", sortable: false },
          { text: "Dog ID#", align: "left", value: "dogId", width: "100px", sortable: false },
          { text: "Dog Name", align: "left", value: "dogName", width: "200px", sortable: false },
          { text: "Access dates", align: "left", value: "accessStartsAt", width: "200px", sortable: false },
          { text: "", align: "left", value: "actionButton", width: "100px", sortable: false },
        ],
      };
    },
    created() {
      this.loadMyPedigreesForInactiveFilter = debounce(0, () => this.loadMyPedigrees());
      this.loadMyPedigreesForDogFilter = debounce(1000, () => this.loadMyPedigrees());
      this.loadMyPedigreesForNameFilter = debounce(1500, () => this.loadMyPedigrees());
    },
    mounted: function () {
      this.loadMyPedigrees();
    },
    beforeDestroy() {
      this.loadMyPedigreesForInactiveFilter?.cancel();
      this.loadMyPedigreesForDogFilter?.cancel();
      this.loadMyPedigreesForNameFilter?.cancel();
    },
    computed: {
      userInfo(): UserInfo {
        return this.$store.state.userInfo;
      },
      reportViewer(): any {
        return this.$refs.reportViewer;
      }
    },
    watch: {      
      dogId: function(newValue, oldValue) {
        if (newValue !== oldValue && newValue !== this.searchParams.dogId) {
          this.searchParams.dogId = newValue ?? "";
        }
      },
      userInfo: function (newVal: object) { 
        this.loadMyPedigrees();
      },    
      "searchParams.includeInactive": function(newValue, oldValue) {
        if (newValue === oldValue) return;
        this.invalidatePedigreeLoad();
        this.schedulePedigreeLoad(this.loadMyPedigreesForInactiveFilter);
      },
      "searchParams.dogId": function(newValue, oldValue) {
        if (newValue === oldValue) return;
        this.invalidatePedigreeLoad();
        this.schedulePedigreeLoad(this.loadMyPedigreesForDogFilter);
      },
      "searchParams.dogName": function(newValue, oldValue) {
        if (newValue === oldValue) return;
        this.invalidatePedigreeLoad();
        this.schedulePedigreeLoad(this.loadMyPedigreesForNameFilter);
      },
    },
    methods: {
      invalidatePedigreeLoad() {
        this.loadRequestId++;
        this.loading = false;
        this.paginatedResponse = { data: [], page: 0, totalCount: 0 };
        this.isError = false;
        this.errorMessage = "";
      },
      cancelPendingPedigreeLoads() {
        this.loadMyPedigreesForInactiveFilter.cancel({ upcomingOnly: true });
        this.loadMyPedigreesForDogFilter.cancel({ upcomingOnly: true });
        this.loadMyPedigreesForNameFilter.cancel({ upcomingOnly: true });
      },
      schedulePedigreeLoad(loader: Function) {
        this.cancelPendingPedigreeLoads();
        loader();
      },
      loadMyPedigreesImmediately() {
        this.cancelPendingPedigreeLoads();
        this.invalidatePedigreeLoad();
        return this.loadMyPedigrees();
      },
      clearDogNameFilter() {
        this.searchParams.dogName = "";
        this.$nextTick(() => this.loadMyPedigreesImmediately());
      },
      loadMyPedigrees: async function()  {
        if (!this.userInfo?.personId) {
          this.invalidatePedigreeLoad();
          return;
        }

        const requestId = ++this.loadRequestId;
        this.loading = true;
        this.isError = false;
        this.errorMessage = "";

      const { page, itemsPerPage } = this.options;
      try {
        const response = await pedigreeService.listMyPedigrees(
          { ...this.options,
            page: (page ?? 1) - 1,
            itemsPerPage: itemsPerPage ?? 10 }
          ,
          { ...this.searchParams }
        );

          if (requestId !== this.loadRequestId) return;
          this.paginatedResponse = response;
        } catch (ex: any) {
          if (requestId !== this.loadRequestId) return;
          this.isError = true;

          this.errorMessage = getErrorMessage(ex);
        } finally{
          if (requestId === this.loadRequestId) this.loading = false;
        }
      },
      showReport: function (dogId: string, reportType: number){
          if(lookupsService.isDogReportTypePedigree(reportType)){
            this.Report_Name = "Asca.Api.Reports.rpt3240_Dog_Pedigree_Report_New2.rdlx";
            this.Report_URL = `/reports/pedigree/${encodeURIComponent(dogId.trim())}`;
          } else if (lookupsService.isDogReportTypeProgeny(reportType)) {
            this.Report_Name = "Asca.Api.Reports.rpt3250_Dog_Progeny_Report.rdlx";
            this.Report_URL = `/reports/progeny/${encodeURIComponent(dogId.trim())}`;
          }
          this.openedDogId = dogId
          this.openedReportType = reportType;
          
          this.showReportDialog = true;
        },  
      downloadReport(dogId: string, reportType: number){
          let fileName = getUserToday().toString();
          if(lookupsService.isDogReportTypePedigree(reportType)){
            fileName = `${fileName}_Pedigree_${dogId.trim()}`;
          } else if (lookupsService.isDogReportTypeProgeny(reportType)) {
            fileName = `${fileName}_Progeny_${dogId.trim()}`;
          }
                    
          this.reportViewer.exportToPdf(fileName);                
        },  
    }
  });
  </script>
